// pages/admin/AdminDashboard.tsx
import { CategoryScale, Chart, Legend, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row } from "reactstrap";
import {
  getAllOrdersAPI,
} from "../../../api/adminOrderAPIs.ts"; // Adjust paths
import {
  getAllPaymentsAPI,
} from "../../../api/adminPaymentAPIs.ts";
import {
  getAllReturnsAPI,
} from "../../../api/adminReturnExchange.ts";
import {
  getAllShippingsAPI,
} from "../../../api/adminShipping.ts";
import { getAllCategories } from "../../../api/categoryAPIs";
import { getAllContactsAPI } from "../../../api/contactAPIs.ts";
import { getAllProducts } from "../../../api/productAPIs";
import { getAllReviewsAPI } from "../../../api/reviewAPis.ts";
import { getAllSubCategories } from "../../../api/subCategoryAPIs";
import { getAllUsers } from "../../../api/usersAPIs";
import { Icon } from "../../../components/Component"; // Adjust path
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const AdminDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [returns, setReturns] = useState([]);
  const [shippings, setShippings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("30"); // Default to last 30 days
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [
          ordersResponse,
          paymentsResponse,
          returnsResponse,
          shippingsResponse,
          categoriesResponse,
          contactsResponse,
          productsResponse,
          reviewsResponse,
          usersResponse,
        ] = await Promise.all([
          getAllOrdersAPI(),
          getAllPaymentsAPI(),
          getAllReturnsAPI(),
          getAllShippingsAPI(),
          getAllCategories(),
          getAllContactsAPI(),
          getAllProducts(),
          getAllReviewsAPI(),
          getAllUsers(),
        ]);

        const subcategoriesResponse = categoriesResponse.data.data.length
          ? await getAllSubCategories({ parentCategoryId: categoriesResponse.data.data[0]._id })
          : { data: { data: [] } };

        setOrders(ordersResponse.data.data);
        setPayments(paymentsResponse.data.data);
        setReturns(returnsResponse.data.data);
        setShippings(shippingsResponse.data.data);
        setCategories(categoriesResponse.data.data);
        setContacts(contactsResponse.data.data);
        setProducts(productsResponse.data.data.products);
        setReviews(reviewsResponse.data.data);
        setSubcategories(subcategoriesResponse.data.data);
        setUsers(usersResponse.data.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Summary Calculations
  const totalSales = payments?.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);
  const pendingOrders = orders.filter((order) => order.orderStatus === "Pending").length;
  const pendingReturns = returns.filter((ret) => ret.status === "Pending").length;
  const pendingShippings = shippings.filter((ship) => ship.shippingStatus === "Pending").length;
  const totalProducts = products.length;
  const totalUsers = users.length;
  const pendingContacts = contacts.length;
  const totalReviews = reviews.length;

  // Chart Data
  const salesChartData = {
    labels: payments.slice(0, 7).map((p) => new Date(p.createdAt).toLocaleDateString()),
    datasets: [{
      label: "Sales",
      data: payments.slice(0, 7).map((p) => Number(p.amount.$numberDecimal)),
      borderColor: "#007bff",
      backgroundColor: "rgba(0, 123, 255, 0.1)",
      borderWidth: 2,
      fill: true,
    }],
  };

  const chartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "#343a40",
        titleFont: { size: "12px" },
        bodyFont: { size: "12px" },
        padding: 8,
      },
    },
    maintainAspectRatio: false,
    scales: {
      y: { display: false },
      x: { display: false },
    },
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  if (loading) {
    return (
      <React.Fragment>
        <Head title="E-Commerce Admin Dashboard" />
        <Content>
          <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3 className="text-primary mb-1">E-Commerce Dashboard</h3>
                <small className="text-muted">Real-time insights into your store's performance</small>
              </div>
            </div>
            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
              <div className="text-center">
                <div
                  className="spinner-grow text-primary"
                  style={{ width: "3rem", height: "3rem" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading dashboard data, please wait...</p>
              </div>
            </div>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title="E-Commerce Admin Dashboard" />
      <Content>
        <div className="container-fluid py-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="text-primary mb-1">E-Commerce Dashboard</h3>
              <small className="text-muted">Real-time insights into your store's performance</small>
            </div>
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
              <DropdownToggle color="primary" caret>
                Last {timeFilter} Days
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => setTimeFilter("7")}>Last 7 Days</DropdownItem>
                <DropdownItem onClick={() => setTimeFilter("30")}>Last 30 Days</DropdownItem>
                <DropdownItem onClick={() => setTimeFilter("90")}>Last 90 Days</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* Summary Cards */}
          <Row className="mb-4">
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-2">Total Sales</h6>
                      <h4 className="text-primary mb-0">₹{totalSales.toFixed(2)}</h4>
                    </div>
                    <div className="text-success">
                      <Icon name="arrow-up" /> 5.2%
                    </div>
                  </div>
                  <div className="mt-3" style={{ height: "80px" }}>
                    <Line data={salesChartData} options={chartOptions} />
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <h6 className="text-muted mb-2">Pending Orders</h6>
                  <h4 className="text-primary mb-3">{pendingOrders}</h4>
                  <p className="text-danger mb-0"><Icon name="alert-fill" /> Action Required</p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <h6 className="text-muted mb-2">Pending Returns</h6>
                  <h4 className="text-primary mb-3">{pendingReturns}</h4>
                  <p className="text-danger mb-0"><Icon name="alert-fill" /> Review Needed</p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <h6 className="text-muted mb-2">Pending Shipments</h6>
                  <h4 className="text-primary mb-3">{pendingShippings}</h4>
                  <p className="text-danger mb-0"><Icon name="alert-fill" /> Ship Now</p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <h6 className="text-muted mb-2">Total Products</h6>
                  <h4 className="text-primary mb-3">{totalProducts}</h4>
                  <p className="text-success mb-0"><Icon name="plus" /> Add More</p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <h6 className="text-muted mb-2">Total Users</h6>
                  <h4 className="text-primary mb-3">{totalUsers}</h4>
                  <p className="text-success mb-0"><Icon name="users" /> Growing</p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <h6 className="text-muted mb-2">Pending Contacts</h6>
                  <h4 className="text-primary mb-3">{pendingContacts}</h4>
                  <p className="text-danger mb-0"><Icon name="alert-fill" /> Respond Soon</p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <CardBody>
                  <h6 className="text-muted mb-2">Total Reviews</h6>
                  <h4 className="text-primary mb-3">{totalReviews}</h4>
                  <p className="text-success mb-0"><Icon name="star" /> Customer Feedback</p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Recent Activity */}
          <Row>
            <Col xl={6} className="mb-4">
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="text-dark font-weight-bold">Recent Orders</h5>
                    <Button color="primary" size="sm">View All</Button>
                  </div>
                  <div>
                    {orders.slice(0, 5).map((order) => (
                      <div key={order._id} className="d-flex justify-content-between align-items-center py-3 border-bottom">
                        <div>
                          <p className="text-primary mb-1">{order._id}</p>
                          <small className="text-muted">{`${order.user_id.first_name} ${order.user_id.last_name}`}</small>
                        </div>
                        <div className="text-right">
                          <p className="text-dark mb-1">₹{order.totalAmount}</p>
                          <span className={`badge ${order.orderStatus === "Delivered" ? "bg-success" : "bg-warning"} text-white`}>
                            {order.orderStatus}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={6} className="mb-4">
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="text-dark font-weight-bold">Recent Payments</h5>
                    <Button color="primary" size="sm">View All</Button>
                  </div>
                  <div>
                    {payments.slice(0, 5).map((payment) => (
                      <div key={payment._id} className="d-flex justify-content-between align-items-center py-3 border-bottom">
                        <div>
                          <p className="text-primary mb-1">{payment._id}</p>
                          <small className="text-muted">{`${payment.userId.first_name} ${payment.userId.last_name}`}</small>
                        </div>
                        <div className="text-right">
                          <p className="text-dark mb-1">₹{Number(payment.amount).toFixed(2)}</p>
                          <span className={`badge ${payment.status === "Completed" ? "bg-success" : "bg-warning"} text-white`}>
                            {payment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={6} className="mb-4">
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="text-dark font-weight-bold">Recent Returns</h5>
                    <Button color="primary" size="sm">View All</Button>
                  </div>
                  <div>
                    {returns.slice(0, 5).map((ret) => (
                      <div key={ret._id} className="d-flex justify-content-between align-items-center py-3 border-bottom">
                        <div>
                          <p className="text-primary mb-1">{ret._id}</p>
                          <small className="text-muted">{`${ret.userId.first_name} ${ret.userId.last_name}`}</small>
                        </div>
                        <div className="text-right">
                          <p className="text-dark mb-1">{ret.productId.name}</p>
                          <span className={`badge ${ret.status === "Approved" ? "bg-success" : "bg-warning"} text-white`}>
                            {ret.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={6} className="mb-4">
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="text-dark font-weight-bold">Recent Shipments</h5>
                    <Button color="primary" size="sm">View All</Button>
                  </div>
                  <div>
                    {shippings.slice(0, 5).map((ship) => (
                      <div key={ship._id} className="d-flex justify-content-between align-items-center py-3 border-bottom">
                        <div>
                          <p className="text-primary mb-1">{ship._id}</p>
                          <small className="text-muted">{`${ship.userId.first_name} ${ship.userId.last_name}`}</small>
                        </div>
                        <div className="text-right">
                          <p className="text-dark mb-1">{ship.orderId?._id || "N/A"}</p>
                          <span className={`badge ${ship.shippingStatus === "Delivered" ? "bg-success" : "bg-warning"} text-white`}>
                            {ship.shippingStatus}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Content>
    </React.Fragment>
  );
};

export default AdminDashboard;